import { useState, useMemo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/ui/empty-state';
import {
  JackpotCard,
  type RodeoRegistrationSummary,
  type BuddyGroupCardAction,
} from '@/components/jackpots/JackpotCard';
import {
  RodeoCalendarView,
  type CalendarRodeoUserEvent,
} from '@/components/rodeos/RodeoCalendarView';
import { useRodeosList } from '@/api/extensions/useRodeoOps';
import { useMyRegistrations } from '@/api/extensions/useRegistrationOps';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useBuddyGroups } from '@/hooks/useBuddyGroups';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import { isScheduleItemPast } from '@/utils/timezone';

type TabValue = 'upcoming' | 'past';
const PAGE_SIZE = 12;

function categorize(rodeos: any[]): Record<TabValue, any[]> {
  const result: Record<TabValue, any[]> = { upcoming: [], past: [] };

  // A rodeo is "past" only once today has moved beyond its last running day
  // IN THE RODEO'S OWN TIMEZONE — a rodeo still running tonight stays
  // "upcoming" through 11:59pm local and only flips to past at midnight there.
  // Anything scheduled for the future is "upcoming".
  for (const r of rodeos) {
    if (
      isScheduleItemPast({
        startDate: r.start_date,
        endDate: r.end_date,
        timezone: r.timezone,
      })
    ) {
      result.past.push(r);
    } else {
      result.upcoming.push(r);
    }
  }

  // Sort: upcoming ascending (soonest/in-progress first), past descending
  // (most recent first). Rodeos missing a start_date sink to the bottom.
  const startKey = (r: any) =>
    typeof r.start_date === 'string' ? r.start_date.substring(0, 10) : '';
  const sortByStart = (dir: 'asc' | 'desc') => (a: any, b: any) => {
    const ka = startKey(a);
    const kb = startKey(b);
    if (!ka && !kb) return 0;
    if (!ka) return 1;
    if (!kb) return -1;
    const cmp = ka.localeCompare(kb);
    return dir === 'asc' ? cmp : -cmp;
  };
  result.upcoming.sort(sortByStart('asc'));
  result.past.sort(sortByStart('desc'));

  return result;
}

export default function RodeosPage() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.role?.role_name === 'admin');

  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const stored = sessionStorage.getItem('rodeos-tab');
    // Migrate legacy 'current' value (tab was removed) → 'upcoming'.
    if (stored === 'upcoming' || stored === 'past') return stored;
    return 'upcoming';
  });
  const [showCalendar, setShowCalendar] = useState<boolean>(() => {
    try {
      return localStorage.getItem('rodeos-show-calendar') === 'true';
    } catch {
      return false;
    }
  });
  const [pages, setPages] = useState<Record<TabValue, number>>({
    upcoming: 1,
    past: 1,
  });

  const handleShowCalendarChange = (next: boolean) => {
    setShowCalendar(next);
    try {
      localStorage.setItem('rodeos-show-calendar', String(next));
    } catch {
      // no-op: persistence is best-effort
    }
  };

  const userProfileId = useAuthStore((s) => s.profile?.id ?? null);
  const { data, isLoading } = useRodeosList({ is_published: true, page_size: 100 });
  // include_buddy_group surfaces teammate rows for primary cardholders so
  // the rodeo-card "group has X registrations" line is accurate. Non-primary
  // members only see their own row, but they still get the self count and
  // the "buddy group attached" cue.
  const { data: myRegistrations } = useMyRegistrations({ include_buddy_group: true });
  const { data: myBuddyGroups } = useBuddyGroups();
  // Resolve primary_organization_id → name on the client. The /jackpot-ops/list
  // backend selects rodeos.* without joining organizations, so the rodeo
  // payload only carries the org UUID; we hydrate the display name here so
  // both the cards and the calendar widget can show the host org.
  const { data: myOrganizations } = useOrganizations();
  const allRodeos = useMemo(() => {
    const list = (data?.data ?? []) as any[];
    const orgNameById = new Map<string, string>();
    for (const o of (myOrganizations ?? []) as Array<{
      id?: string;
      name?: string;
    }>) {
      if (o?.id && o?.name) orgNameById.set(o.id, o.name);
    }
    return list.map((r: any) => ({
      ...r,
      organization_name:
        r.organization_name ??
        (typeof r.primary_organization_id === 'string'
          ? orgNameById.get(r.primary_organization_id)
          : undefined) ??
        null,
    }));
  }, [data?.data, myOrganizations]);

  const categorized = useMemo(() => categorize(allRodeos), [allRodeos]);

  // Build a per-rodeo registration summary used by JackpotCard. We pre-
  // aggregate here (rather than letting the card refetch) because the
  // rodeos list page can render dozens of cards and a single source of
  // truth keeps the per-card render pure.
  const rodeoSummaries = useMemo(() => {
    const map = new Map<string, RodeoRegistrationSummary>();
    if (!userProfileId) return map;

    // Index buddy groups by every rodeo they're attached to. Buddy
    // groups are M:N with rodeos now, so a single group can show up
    // under multiple rodeos. We read from `attached_rodeo_ids` (the
    // authoritative junction-table list); the legacy singular
    // `rodeo_id` column only holds whichever rodeo was attached first
    // and would silently drop the rest.
    const groupByRodeo = new Map<
      string,
      { name: string | null; id: string }
    >();
    for (const g of myBuddyGroups ?? []) {
      if (g.status === 'disbanded') continue;
      const attachedIds =
        g.attached_rodeo_ids && g.attached_rodeo_ids.length > 0
          ? g.attached_rodeo_ids
          : g.rodeo_id
            ? [g.rodeo_id]
            : [];
      for (const rid of attachedIds) {
        groupByRodeo.set(rid, { name: g.group_name ?? null, id: g.id });
      }
    }

    // Build event_id → rodeo_id index for the visible rodeos so we can
    // bucket registrations by rodeo without hitting the events table.
    const eventToRodeo = new Map<string, string>();
    for (const r of allRodeos) {
      for (const e of (r.events ?? []) as Array<{ id: string }>) {
        if (e?.id) eventToRodeo.set(e.id, r.id);
      }
    }

    // Walk the user's registrations once and accumulate per-rodeo stats.
    type Bucket = {
      selfEvents: Set<string>;
      groupRegs: number;
      groupEvents: Set<string>;
    };
    const buckets = new Map<string, Bucket>();
    const ensure = (rodeoId: string): Bucket => {
      let b = buckets.get(rodeoId);
      if (!b) {
        b = { selfEvents: new Set(), groupRegs: 0, groupEvents: new Set() };
        buckets.set(rodeoId, b);
      }
      return b;
    };

    for (const reg of (myRegistrations ?? []) as Array<{
      event_id?: string;
      user_profile_id?: string;
      buddy_group_id?: string | null;
      trig_status_name?: string;
    }>) {
      if (!reg.event_id) continue;
      if (reg.trig_status_name === 'cancelled') continue;
      const rodeoId = eventToRodeo.get(reg.event_id);
      if (!rodeoId) continue;
      const bucket = ensure(rodeoId);
      if (reg.user_profile_id === userProfileId) {
        bucket.selfEvents.add(reg.event_id);
      }
      // Count any registration tied to a buddy group attached to this
      // rodeo — covers both self and teammate rows.
      const attachedGroup = groupByRodeo.get(rodeoId);
      if (attachedGroup && reg.buddy_group_id === attachedGroup.id) {
        bucket.groupRegs += 1;
        bucket.groupEvents.add(reg.event_id);
      }
    }

    for (const r of allRodeos) {
      const bucket = buckets.get(r.id);
      const attachedGroup = groupByRodeo.get(r.id);
      const selfEvents = bucket?.selfEvents.size ?? 0;
      const groupRegsTotal = bucket?.groupRegs ?? 0;
      const groupEventCount = bucket?.groupEvents.size ?? 0;
      // Only emit a summary when there's something meaningful to show —
      // skips an empty banner on rodeos the user hasn't engaged with.
      if (!attachedGroup && selfEvents === 0) continue;
      map.set(r.id, {
        selfEvents,
        groupAttached: !!attachedGroup,
        groupName: attachedGroup?.name ?? null,
        groupRegistrations: attachedGroup
          ? { totalRegs: groupRegsTotal, eventCount: groupEventCount }
          : null,
      });
    }
    return map;
  }, [allRodeos, myRegistrations, myBuddyGroups, userProfileId]);

  // Per-rodeo buddy-group action state — drives the "Register / Manage /
  // Create" CTA on each rodeo card so users can act without drilling into
  // the detail page. Mirrors the four states from RodeoDetailPage but for
  // every visible rodeo at once. We only emit entries for rodeos with
  // buddy_groups_enabled — the card itself also gates on that flag, but
  // skipping here keeps the map small and avoids confusing CTAs on jackpots
  // that happen to flow through this component in the future.
  type BuddyGroupActionInfo = {
    state: BuddyGroupCardAction['state'];
    groupId: string | null;
    groupName: string | null;
    eligibleCount: number;
    memberOnlyCount: number;
  };
  const buddyGroupActions = useMemo(() => {
    const map = new Map<string, BuddyGroupActionInfo>();
    if (!userProfileId || !myBuddyGroups) return map;

    // Index every buddy group the user is in by every rodeo it's attached
    // to. Buddy groups are M:N with rodeos: prefer `attached_rodeo_ids`
    // (authoritative) and fall back to the legacy singular `rodeo_id`
    // when the group hasn't been migrated yet.
    type Attachment = {
      groupId: string;
      groupName: string | null;
      isPrimary: boolean;
    };
    const attachmentByRodeo = new Map<string, Attachment>();
    for (const g of myBuddyGroups) {
      if (g.status === 'disbanded') continue;
      const rids =
        g.attached_rodeo_ids && g.attached_rodeo_ids.length > 0
          ? g.attached_rodeo_ids
          : g.rodeo_id
            ? [g.rodeo_id]
            : [];
      for (const rid of rids) {
        // Double-entry validation prevents the same user being in two
        // groups attached to one rodeo, so first-write-wins is fine.
        if (!attachmentByRodeo.has(rid)) {
          attachmentByRodeo.set(rid, {
            groupId: g.id,
            groupName: g.group_name ?? null,
            isPrimary: g.is_primary,
          });
        }
      }
    }

    // "Eligible to register" = any non-disbanded group the user owns. We
    // don't filter by other-rodeo attachments because the same group can
    // register for any number of rodeos (the setup page handles per-rodeo
    // already-registered detection).
    const eligibleCount = myBuddyGroups.filter(
      (g) => g.is_primary && g.status !== 'disbanded',
    ).length;
    // Groups the user is in but where they are NOT the primary cardholder.
    // Surfaces a "you're already in a group — ask your primary" hint on
    // rodeos this user can't act on themselves, so the must_create copy
    // doesn't lie to members ("No buddy group yet") when they actually
    // have one. Mirrors the detail page's memberOnlyGroups treatment.
    const memberOnlyCount = myBuddyGroups.filter(
      (g) => !g.is_primary && g.status !== 'disbanded',
    ).length;

    for (const r of allRodeos) {
      // Finals rodeos run every contestant in every performance — there is
      // nothing for a buddy group to co-schedule, and the backend rejects
      // the create/join/attach calls, so don't offer the CTA at all.
      if (!r.buddy_groups_enabled || (r as any).is_finals === true) continue;
      const att = attachmentByRodeo.get(r.id);
      if (att) {
        map.set(r.id, {
          state: att.isPrimary ? 'registered_primary' : 'registered_member',
          groupId: att.groupId,
          groupName: att.groupName,
          eligibleCount,
          memberOnlyCount,
        });
      } else if (eligibleCount > 0) {
        map.set(r.id, {
          state: 'can_register',
          groupId: null,
          groupName: null,
          eligibleCount,
          memberOnlyCount,
        });
      } else {
        map.set(r.id, {
          state: 'must_create',
          groupId: null,
          groupName: null,
          eligibleCount: 0,
          memberOnlyCount,
        });
      }
    }
    return map;
  }, [allRodeos, myBuddyGroups, userProfileId]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabValue);
    sessionStorage.setItem('rodeos-tab', tab);
  };

  const paginate = (items: any[], tab: TabValue) => {
    const page = pages[tab];
    const start = (page - 1) * PAGE_SIZE;
    return {
      items: items.slice(start, start + PAGE_SIZE),
      totalPages: Math.ceil(items.length / PAGE_SIZE),
      page,
    };
  };

  const setPage = (tab: TabValue, p: number) => {
    setPages((prev) => ({ ...prev, [tab]: p }));
  };

  // Past rodeos shouldn't surface register/create CTAs — there's nothing
  // useful to register for. We still let registered_primary / member render
  // so users can review historical group state if they want.
  const buildBuddyGroupAction = (
    rodeoId: string,
    tab: TabValue,
  ): BuddyGroupCardAction | null => {
    const info = buddyGroupActions.get(rodeoId);
    if (!info) return null;
    if (
      tab === 'past' &&
      (info.state === 'can_register' || info.state === 'must_create')
    ) {
      return null;
    }
    // For users who are in a member-only group AND have no primary
    // groups, the must_create state surfaces TWO actions: a "View" path
    // to /my/buddy-groups (so they can find their primary cardholder
    // and follow up) and the regular "Create" path to the setup page.
    const showSecondary =
      info.state === 'must_create' && info.memberOnlyCount > 0;
    return {
      state: info.state,
      attachedGroupName: info.groupName,
      eligibleCount: info.eligibleCount,
      memberOnlyCount: info.memberOnlyCount,
      onAction: (e) => {
        e.stopPropagation();
        sessionStorage.setItem('rodeos-scroll', String(window.scrollY));
        if (info.state === 'registered_member' && info.groupId) {
          navigate(`/my/buddy-groups/${info.groupId}`);
        } else {
          navigate(`/rodeos/${rodeoId}/buddy-group/setup`);
        }
      },
      ...(showSecondary
        ? {
            onSecondaryAction: (e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              sessionStorage.setItem(
                'rodeos-scroll',
                String(window.scrollY),
              );
              navigate('/my/buddy-groups');
            },
          }
        : {}),
    };
  };

  const renderTab = (tab: TabValue) => {
    const items = categorized[tab];
    const { items: pageItems, totalPages, page } = paginate(items, tab);

    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      );
    }

    if (pageItems.length === 0) {
      return (
        <EmptyState
          icon={Search}
          title={`No ${tab} rodeos`}
          description={
            tab === 'upcoming'
              ? 'Check back soon for new rodeos.'
              : 'No past rodeos found.'
          }
        />
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pageItems.map((r: any) => (
            <JackpotCard
              key={r.id}
              jackpot={r}
              onClick={() => {
                sessionStorage.setItem('rodeos-scroll', String(window.scrollY));
                navigate(`/rodeos/${r.id}`);
              }}
              showPublished={isAdmin}
              registrationSummary={rodeoSummaries.get(r.id) ?? null}
              buddyGroupAction={buildBuddyGroupAction(r.id, tab)}
              viewActionLabel="View Rodeo Events"
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-xs text-muted-foreground">
              Page {page} of {totalPages} ({items.length} total)
            </p>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(tab, page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(tab, page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  // Calendar widget shows the current + next 2 months so attendees can see
  // at a glance which days have rodeos. The "upcoming" bucket now includes
  // any rodeo whose last day is today or later (in-progress events stay in
  // upcoming until their final day passes), so feeding it alone covers
  // everything the calendar should highlight. We also annotate each rodeo
  // with the user's own registered events so the day-popover can list
  // "Your events" inline. The host org name is already hydrated on every
  // rodeo by the `allRodeos` memo above, so we just thread it through here.
  const calendarRodeos = useMemo(() => {
    const list = [...categorized.upcoming];
    const ownRegByEventId = new Map<
      string,
      { position?: string | null }
    >();
    if (userProfileId && myRegistrations) {
      for (const reg of myRegistrations as Array<{
        event_id?: string;
        user_profile_id?: string;
        position?: string | null;
        trig_status_name?: string;
      }>) {
        if (!reg.event_id) continue;
        if (reg.user_profile_id !== userProfileId) continue;
        if (reg.trig_status_name === 'cancelled') continue;
        ownRegByEventId.set(reg.event_id, { position: reg.position ?? null });
      }
    }
    return list.map((r: any) => {
      const events = (r.events ?? []) as Array<Record<string, unknown>>;
      const userRegisteredEvents: CalendarRodeoUserEvent[] = [];
      for (const e of events) {
        const eventId = typeof e.id === 'string' ? e.id : '';
        if (!eventId) continue;
        const reg = ownRegByEventId.get(eventId);
        if (!reg) continue;
        const title = getEventDisplayTitle(e as any);
        const label = reg.position ? `${title} — ${reg.position}` : title;
        userRegisteredEvents.push({ eventId, label });
      }
      return {
        ...r,
        ...(userRegisteredEvents.length > 0 ? { userRegisteredEvents } : {}),
      };
    });
  }, [categorized.upcoming, myRegistrations, userProfileId]);

  // Rodeos the user has personal registrations in — drives the red dot in
  // the calendar so people can see at a glance which days they need to
  // show up. Buddy-group-only attachments don't count here; we only mark
  // days where the user themselves is on the registration list.
  const registeredRodeoIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [rodeoId, summary] of rodeoSummaries) {
      if (summary.selfEvents > 0) ids.add(rodeoId);
    }
    return ids;
  }, [rodeoSummaries]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {activeTab === 'past' ? 'Past Rodeos' : 'Upcoming Rodeos'}
        </h1>
      </div>

      {!isLoading && showCalendar && (
        <RodeoCalendarView
          rodeos={calendarRodeos}
          registeredRodeoIds={registeredRodeoIds}
        />
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="upcoming" className="gap-1.5">
              Upcoming
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                {categorized.upcoming.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="past" className="gap-1.5">
              Past
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                {categorized.past.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
          <label
            htmlFor="rodeos-calendar-toggle"
            className="flex items-center gap-2 text-sm text-muted-foreground select-none"
          >
            <Switch
              id="rodeos-calendar-toggle"
              checked={showCalendar}
              onCheckedChange={handleShowCalendarChange}
              aria-label="Show calendar"
            />
            <span>Show calendar</span>
          </label>
        </div>
        <TabsContent value="upcoming" className="mt-4">
          {renderTab('upcoming')}
        </TabsContent>
        <TabsContent value="past" className="mt-4">
          {renderTab('past')}
        </TabsContent>
      </Tabs>
    </div>
  );
}
